import * as React from 'react'
import {
  VariantData,
  WorkflowBlock,
  getVariantsDataFromKey,
  isVariantSetBlock,
  isWorkflowBlock,
  withUniqueApiNames,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { useMemo } from 'react'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { getBackgroundColor, getSolidColor } from './variant-set/utils'
import { Spinner } from '~/components/misc/spinner'
import { invariant } from '@epic-web/invariant'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { WorkflowRunIcon } from './workflow/run-icon'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useRepoStore } from '../repo-store'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useGetTabPrefix, useTabsStore } from '../tabs/store'

export const VariantPills = ({
  variants,
  uppercase = true,
  blockId,
}: {
  variants: VariantData[] | string
  uppercase?: boolean
  blockId?: string
}) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const validVariants = useMemo(
    () =>
      (typeof variants === 'string'
        ? getVariantsDataFromKey(variants)
        : variants
      )
        .map((set) => {
          const node = getNode?.(set.setId, {
            fallbackKey: 'sourceBlockId',
          })?.value
          if (!node || !isVariantSetBlock(node)) {
            return null
          }
          const match = withUniqueApiNames(node.value.variants).find(
            (v) => v.id === set.variantId
          )
          if (!match) return null
          return {
            variantId: set.variantId,
            color: match.color,
            label: match.label,
            setId: set.setId,
          }
        })
        .filter(Boolean),
    [getNode, variants]
  )

  return validVariants.map((data) => (
    <VariantPill
      key={data.variantId}
      variantData={data}
      uppercase={uppercase}
      blockId={blockId}
    />
  ))
}

const VariantPill = ({
  variantData,
  uppercase,
  blockId,
}: {
  variantData: {
    variantId: string
    color: string
    label: string
    setId: string
  }
  uppercase: boolean
  blockId?: string
}) => {
  const setFocus = useRepoStore((s) => s.setFocus)
  const tabPrefix = useGetTabPrefix()
  const addTab = useTabsStore((s) => s.addTab)
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const run:
    | (WorkflowBlock['value']['activeRuns'][number] & {
        workflowBlockId: string
      })
    | undefined = useWorkingTreeStore((s) => {
    if (!blockId) return

    const blocksByTypeIndex = s.tree?.compoundIndexes.get('blocksByType')
    if (!blocksByTypeIndex) return

    const workflowBlockIds = Array.from(
      blocksByTypeIndex?.get('workflow') ?? new Set<string>()
    )

    const generationStatus = workflowBlockIds
      ?.map((id) => {
        const workflowBlock = s.tree?.getNode(id)?.value
        if (!workflowBlock) return

        invariant(
          isWorkflowBlock(workflowBlock),
          `Expected workflow block, got: ${workflowBlock.type}`
        )

        const activeRunForThisBlockAndVariant =
          workflowBlock.value.activeRuns.find((run) => {
            return (
              run.trigger.type === 'layout-block.updated' &&
              run.action.type === 'ai-autofill-variant' &&
              run.action.blockId === variantData.setId &&
              run.trigger.data.layoutBlockId === blockId
            )
          })

        return activeRunForThisBlockAndVariant
          ? {
              ...activeRunForThisBlockAndVariant,
              workflowBlockId: idToSourceBlockId(workflowBlock),
            }
          : undefined
      })
      .filter(Boolean)

    return generationStatus[0]
  })

  const workflowBlockParentId = React.useMemo(() => {
    if (!getNode || !run) return undefined
    const workflowBlock = getNode(run.workflowBlockId)
    if (!workflowBlock || !workflowBlock.parentKey) return null
    return idToSourceBlockId(workflowBlock.parentKey, { getNode })
  }, [getNode, run])
  const workflowSourceBlockId =
    getNode && run
      ? idToSourceBlockId(run.workflowBlockId, { getNode })
      : undefined

  return (
    <div
      className="flex items-center border rounded h-5"
      style={{ borderColor: getSolidColor(variantData.color) }}
    >
      <div
        style={{
          backgroundColor: getBackgroundColor(variantData.color),
          color: getSolidColor(variantData.color),
        }}
        className={clsx(
          'h-5 flex px-1.5 relative rounded-l-[3px]',
          'light:!text-dark-bg text-dark-faint text-[11px] leading-5',
          'duration-200 transition-colors max-w-[160px]',
          uppercase ? 'uppercase font-semibold' : 'font-medium',
          !run && 'rounded-r-[3px]'
        )}
      >
        <span className="truncate">{variantData.label}</span>
      </div>

      {!!run && (
        <ButtonLink
          unstyled
          href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${workflowBlockParentId}#${workflowSourceBlockId}`}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.stopPropagation()
            if (!workflowSourceBlockId) return

            setFocus({
              type: 'block:wrapper',
              blockId: workflowSourceBlockId,
            })
            addTab({
              key: `${tabPrefix}${workflowBlockParentId}`,
              partiallyOpen: true,
            })
          }}
          style={{
            color: getSolidColor(variantData.color),
            ['--background-color' as string]: `rgb(${variantData.color} / 0.2)`,
          }}
          className="hover:underline border-l flex items-center rounded-l-none rounded-r-[3px] -mr-px h-5 px-1 border border-transparent leading-5 text-xs bg-[var(--background-color)] font-medium"
        >
          {run.status === 'running' ? 'Generating' : 'Scheduled'}
          &nbsp;
          {run.status === 'running' ? (
            <Spinner className="w-3" />
          ) : (
            <WorkflowRunIcon
              style={{ color: getSolidColor(variantData.color) }}
              className="!size-3.5"
              run={{
                status: 'pending',
                trigger: {
                  type: 'layout-block.updated',
                  data: {
                    layoutBlockId: blockId ?? '',
                    updatedBlockId: '',
                    userId: null,
                  },
                  timestamp: '',
                },
              }}
            />
          )}
        </ButtonLink>
      )}
    </div>
  )
}

export default VariantPills
